/**
 * 将十六进制颜色转换为RGB格式，并可以设置透明度
 *
 * @param hex 十六进制颜色值，例如 '#FFFFFF' 或 '#FFF'
 * @param alpha 透明度，范围 0-1，默认为 1
 * @returns RGB或RGBA颜色字符串，例如 'rgb(255, 255, 255)' 或 'rgba(255, 255, 255, 0.5)'
 */
export const hexToRgb = (hexStr: string, alpha?: number): string => {
  // 移除可能存在的 # 前缀
  let hex = hexStr.replace(/^#/, '');

  // 处理简写形式，例如 #FFF 转为 #FFFFFF
  if (hex?.length === 3) {
    hex = hex
      .split('')
      .map(char => char + char)
      .join('');
  }

  // 解析十六进制值为RGB
  const r = Number.parseInt(hex.substring(0, 2), 16);
  const g = Number.parseInt(hex.substring(2, 4), 16);
  const b = Number.parseInt(hex.substring(4, 6), 16);

  // 如果提供了透明度，返回rgba格式
  if (alpha !== undefined) {
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  // 否则返回rgb格式
  return `rgb(${r}, ${g}, ${b})`;
};

/**
 * 将RGB或RGBA颜色转换为十六进制格式
 *
 * @param rgb RGB或RGBA颜色字符串，例如 'rgb(255, 255, 255)' 或 'rgba(255, 255, 255, 0.5)'
 * @returns 十六进制颜色值，例如 '#FFFFFF'
 */
export const rgbToHex = (rgb: string): string => {
  // 提取RGB值
  const rgbRegex = /rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/i;
  const match = rgb.match(rgbRegex);

  if (!match) {
    throw new Error('无效的RGB或RGBA颜色格式');
  }

  // 将RGB值转换为十六进制
  const r = Number.parseInt(match[1], 10);
  const g = Number.parseInt(match[2], 10);
  const b = Number.parseInt(match[3], 10);

  // 确保值在有效范围内
  const validR = Math.max(0, Math.min(255, r));
  const validG = Math.max(0, Math.min(255, g));
  const validB = Math.max(0, Math.min(255, b));

  // 转换为十六进制并确保每个颜色通道都是两位数
  const toHex = (c: number): string => {
    const hex = c.toString(16);
    return hex.length === 1 ? `0${hex}` : hex;
  };

  return `#${toHex(validR)}${toHex(validG)}${toHex(validB)}`.toUpperCase();
};

export const colors: string[] = [
  // 主色调
  '#5B8FF9', // 主蓝色
  '#5AD8A6', // 清新绿色
  '#F6BD16', // 明黄色
  '#6F5EF9', // 优雅紫色
  '#E8684A', // 珊瑚红
  '#6DC8EC', // 浅蓝色
  '#9270CA', // 淡紫色
  '#FF9D4D', // 温暖橙色
  '#269A99', // 青绿色
  '#FF99C3', // 柔和粉色
  '#A8ABF1', // 灰紫色
  '#5D7092' // 深灰蓝色
];

export const otherColors: any[] = [
  '#a6cee3',
  '#1f78b4',
  '#b2df8a',
  '#33a02c',
  '#fb9a99',
  '#e31a1c',
  '#fdbf6f',
  '#ff7f00',
  '#cab2d6',
  '#6a3d9a',
  '#ffff99',
  '#b15928'
];

/**
 * 从颜色数组中循环获取颜色
 *
 * @param index 索引值
 * @param colorArray 颜色数组，默认为 colors
 * @returns 对应的颜色值
 */
export function getColorByIndex(index: number, colorArray: string[] = colors): string {
  if (!colorArray || colorArray.length === 0) {
    return '#000000'; // 默认黑色
  }

  // 使用取模运算实现循环
  const actualIndex = index % colorArray.length;
  return colorArray[actualIndex];
}
